import { ref, computed } from 'vue'
import api from '@/lib/axios'

const crmUser = ref(null)
const isLoading = ref(false)
const isReady = ref(false)
const error = ref(null)
// HTTP status of the last failed /me, so the router can tell "no session" (401) from
// "signed in but no CRM row" (403). null after a success.
const lastStatus = ref(null)

const role = computed(() => crmUser.value?.role || null)
const roles = computed(() => crmUser.value?.roles || [])
const isSuperAdmin = computed(() => crmUser.value?.role === 'super_admin')
const isManager = computed(() => crmUser.value?.role === 'manager')
const canViewAll = computed(() => isSuperAdmin.value || isManager.value)
const featureFlags = computed(() => crmUser.value?.featureFlags || [])
const department = computed(() => (crmUser.value?.department || '').toUpperCase() || null)
// True for super_admins (cross-dept) and anyone in the Junk department.
// Use this to gate Junk-only features like Request Quote / Submit to Buyer.
const isJunkDept = computed(() => isSuperAdmin.value || department.value === 'JUNK')

// Change-highlight "Updated" badges (dispatch overrides of offer/RV-submitted
// fields). v1: ON for everyone. To restrict to management later, flip the body
// to `canViewAll.value` — no backend or migration change needed.
const canUseChangeHighlight = computed(() => true)

// TEMP preview lock for the Ads Watchdog dashboard. While this list is non-empty,
// only these emails can see/open it (still must be super_admin). Set to [] to open
// it to all super_admins — that's the intended long-term default.
const WATCHDOG_PREVIEW_EMAILS = ['developer@junkcarsolutions.com']
const canViewWatchdog = computed(() => {
  if (!isSuperAdmin.value) return false
  if (WATCHDOG_PREVIEW_EMAILS.length === 0) return true
  return WATCHDOG_PREVIEW_EMAILS.includes(crmUser.value?.email)
})

// Conversion-review swipe deck — pure email allowlist (Samy under any of his logins, plus
// developer@), deliberately with NO super_admin requirement: samy@familycarbuyers.com is a
// `rep`, and gating on the role would hide the deck from him. The backend enforces the same
// list (CONVERSION_REVIEW_EMAILS in routes/conversionReview.js); this only hides the UI.
// Fail-closed: an empty list locks everyone out, and a missing email (`?.`) never matches.
const CONVERSION_REVIEW_EMAILS = [
  'samy@junkcarsolutions.com',
  'sybcars@gmail.com',
  'samy@familycarbuyers.com',
  'developer@junkcarsolutions.com',
]
const canViewConversionReview = computed(() =>
  CONVERSION_REVIEW_EMAILS.includes((crmUser.value?.email || '').toLowerCase())
)

// Auction quote calculator (port of the reps' QUOTE CALCULATOR sheet) — preview lock for JR and
// Samy while it's checked against the sheets. Same people as the conversion-review deck; the
// backend enforces the same list (QUOTE_CALCULATOR_EMAILS in routes/quoteCalculator.js).
// Fail-closed like the lists around it.
const QUOTE_CALCULATOR_EMAILS = [
  'samy@junkcarsolutions.com',
  'sybcars@gmail.com',
  'samy@familycarbuyers.com',
  'developer@junkcarsolutions.com',
]
const canViewQuoteCalculator = computed(() =>
  QUOTE_CALCULATOR_EMAILS.includes((crmUser.value?.email || '').toLowerCase())
)

// Conversations screen (Textline replacement): anyone on at least one texting line, plus
// managers and super_admins, who see every line. The preview email list was removed at
// cutover step C2 (Julian 2026-10-24: invited reps could not see it). No per-user flag or
// preference gates it; the backend scopes every list to the caller's lines.
const canViewConversations = computed(() =>
  (crmUser.value?.smsLines?.length > 0) || canViewAll.value || roles.value.includes('manager')
)

// Deal priority (urgency tier) editing — pure email allowlist, deliberately NO
// super_admin or role bypass. The backend enforces the same list
// (PRIORITY_EDITOR_EMAILS); this only disables the pickers. Fail-closed: an
// empty list locks everyone out, and a missing email (`?.`) never matches.
const PRIORITY_EDITOR_EMAILS = [
  'angel@familycarbuyers.com',
  'daniela@familycarbuyers.com',
  'management@familycarbuyers.com',
  'samy@junkcarsolutions.com',
  'sybcars@gmail.com',
  'samy@familycarbuyers.com',
  'developer@junkcarsolutions.com',
]
const canEditPriority = computed(() =>
  PRIORITY_EDITOR_EMAILS.includes((crmUser.value?.email || '').toLowerCase())
)

// Title-team surfaces (Angel 0806: "they dont need to go to other screens to claim").
// One gate so the Work Queue's Title Work tab and its query can never disagree, and it
// mirrors the /title-queue scope on the backend: org unit TITLE or a title role, plus
// the supervisors who already get the whole board. `team` is the org unit, NOT
// `department` (the vehicle line) — the title team's department is AUCTION.
const isTitleTeam = computed(() => {
  if (isSuperAdmin.value) return true
  if ((crmUser.value?.team || '').toUpperCase() === 'TITLE') return true
  return ['title', 'title_supervisor', 'manager', 'dispatch_admin'].some(r => roles.value.includes(r))
})

// Same shape and same trap as isTitleTeam: `team` is the org unit (EMAIL_RV), NOT
// `department`, which is the vehicle line. Mirrors the backend's emailRvAudience() roster
// and the /api/email-rv role gates — all three must move together.
const isEmailRvTeam = computed(() => {
  if (isSuperAdmin.value) return true
  if ((crmUser.value?.team || '').toUpperCase() === 'EMAIL_RV') return true
  return ['email_rep', 'quoter', 'email_manager'].some(r => roles.value.includes(r))
})

// Can this person work the Supervisor Requests board (claim/resolve), as opposed to only
// filing requests? Quoters ARE the supervisors on this team (Julian, 0813).
const isEmailRvSupervisor = computed(() => {
  if (isSuperAdmin.value) return true
  return ['quoter', 'email_manager', 'manager'].some(r => roles.value.includes(r))
})

/**
 * Check if the current user has a specific role (primary or additional).
 * super_admin always returns true.
 *
 * Only sound for granting access. For a gate that HIDES something from a role,
 * use hasRoleStrict — otherwise super_admins match every role and lose the UI.
 */
function hasRole(roleName) {
  if (isSuperAdmin.value) return true
  return roles.value.includes(roleName)
}

/**
 * Actual role membership, with no super_admin short-circuit.
 */
function hasRoleStrict(roleName) {
  return roles.value.includes(roleName)
}

// Alias for backward compatibility
const currentUser = crmUser

let fetchPromise = null

export function useCurrentUser() {
  /**
   * Fetch the CRM user from /api/users/me.
   * Idempotent — returns immediately if already loaded.
   */
  const fetchCrmUser = async () => {
    // Already loaded
    if (isReady.value) return crmUser.value

    // Already in-flight — reuse the same promise
    if (fetchPromise) return fetchPromise

    isLoading.value = true
    error.value = null

    fetchPromise = api.get('/api/users/me')
      .then(res => {
        crmUser.value = res.data
        isReady.value = true
        lastStatus.value = null
        return crmUser.value
      })
      .catch(err => {
        lastStatus.value = err.response?.status ?? null
        error.value = err.response?.data?.error || err.message
        console.error('Error fetching CRM user:', err)
        return null
      })
      .finally(() => {
        isLoading.value = false
        fetchPromise = null
      })

    return fetchPromise
  }

  const clearCurrentUser = () => {
    crmUser.value = null
    isReady.value = false
    error.value = null
  }

  return {
    crmUser,
    currentUser,
    role,
    roles,
    isSuperAdmin,
    isManager,
    canViewAll,
    canViewWatchdog,
    canViewConversionReview,
    canViewQuoteCalculator,
    canViewConversations,
    canEditPriority,
    featureFlags,
    hasRole,
    hasRoleStrict,
    department,
    isJunkDept,
    isTitleTeam,
    isEmailRvTeam,
    isEmailRvSupervisor,
    canUseChangeHighlight,
    isReady,
    isLoading,
    error,
    lastStatus,
    fetchCrmUser,
    clearCurrentUser,
  }
}
